import { useState, useEffect, useRef } from 'react'

export function useInactivity(timeout = 5000, broadcastChannel = null) {
  const [isInactive, setIsInactive] = useState(false)
  const timer = useRef(null)
  const channelRef = useRef(broadcastChannel)
  const timeoutRef = useRef(timeout)

  // Update refs when they change
  useEffect(() => {
    channelRef.current = broadcastChannel
    timeoutRef.current = timeout
  }, [broadcastChannel, timeout])

  // Shared reset function using refs to avoid stale closures
  const reset = useRef(() => {
    clearTimeout(timer.current)
    setIsInactive(false)
    timer.current = setTimeout(() => setIsInactive(true), timeoutRef.current)
  })

  // Update reset function when needed
  useEffect(() => {
    reset.current = () => {
      clearTimeout(timer.current)
      setIsInactive(false)
      timer.current = setTimeout(() => setIsInactive(true), timeoutRef.current)
    }
  }, [])

  // Listen to local user activity
  useEffect(() => {
    const handleActivity = () => reset.current()

    const events = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart']
    events.forEach(e => window.addEventListener(e, handleActivity))
    handleActivity() // Start the timer

    return () => {
      clearTimeout(timer.current)
      events.forEach(e => window.removeEventListener(e, handleActivity))
    }
  }, [])

  // Listen to BroadcastChannel for remote activity
  useEffect(() => {
    const handleChannelMessage = event => {
      if (event.data.userActivity) {
        reset.current()
      }
    }

    if (channelRef.current) {
      channelRef.current.addEventListener('message', handleChannelMessage)
    }

    return () => {
      if (channelRef.current) {
        channelRef.current.removeEventListener('message', handleChannelMessage)
      }
    }
  }, [])

  return isInactive
}
